import { useState, useContext, useEffect, useRef } from 'react';
import { Button, Card, CardBody, Modal, ModalBody } from 'reactstrap';
import { Loading, Uploading } from '~/shared/components/Loading';
import {
    fetchCampaignPhotoPresignedUrl,
    uploadFile,
    createAlbum,
    deleteAlbum,
    saveCampaignPhoto,
    saveCampaignPhotoCrop,
    removeCampaignPhoto,
    fetchThumbPhotoPresignedUrl,
    saveThumbPhotoCrop,
} from '~/services/campaign';
import Dropzone from 'react-dropzone';
import Cropper from 'react-cropper';
import { CampaignContext } from '~/context/campaign';
import { renderIcon } from '~/shared/components/icons';
import { getFilename } from '~/services/helper';

import imageCompression from 'browser-image-compression';

import defaultProfileImg from '~/shared/img/HHLDefault-01.jpg';

export const resizeImage = async (file: File) => {
    const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 2000,
        useWebWorker: true,
    };
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
};

export const CampaignPhotoUpload = ({ updatePhoto }) => {
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [savingCampaignImage, setSavingCampaignImage] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [showCropper, setShowCropper] = useState(false);
    const [savedPicture, setSavedPicture] = useState(null);
    const [savingCrop, setSavingCrop] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const cropperRef = useRef(null);

    const resizeCropped = async (file: File) => {
        const options = {
            maxSizeMB: 1,
            maxWidthOrHeight: 400,
            useWebWorker: true,
        };
        const compressedFile = await imageCompression(file, options);
        return compressedFile;
    };

    const deleteCampaignPhoto = async () => {
        const campaignId = campaign?.campaignDetail?.campaign_id;
        removeCampaignPhoto(campaignId);
        campaignDispatch({
            type: 'set_campaign_photo_url',
            payload: null,
        });
        setIsDisabled(false);
    };

    const ShowCropper = () => {
        if (!imageUrl) {
            console.log('no resized file');
            return <div />;
        }

        const saveCrop = async (cropString) => {
            if (!savedPicture) {
                return;
            }
            // Pass campaign_id for superadmin functionality
            const campaignId = campaign.campaignDetail.campaign_id;

            try {
                setSavingCrop(true);

                // Establish cropped filename
                const { filename } = savedPicture;
                const filenameParts = filename.split('.');
                const unixSeconds = Math.floor(Date.now() / 1000);
                let croppedFilename =
                    filenameParts[0] +
                    unixSeconds +
                    '_cropped.' +
                    filenameParts[1];
                console.log('croppedFilename: ', croppedFilename);

                // Prepare new signed url
                const { fields, url } = await fetchCampaignPhotoPresignedUrl(
                    croppedFilename,
                    campaignId
                );

                // Grab blob from cropped canvas
                const cropper = cropperRef.current?.cropper;
                const blob = await new Promise((resolve) =>
                    cropper.getCroppedCanvas().toBlob(resolve)
                );

                // Convert blob to file
                const file = new File([blob], croppedFilename, {
                    type: 'image/jpeg',
                });

                // Upload cropped to s3
                const resp = await uploadFile(fields, url, file);

                // Save crop to DB
                const { key } = fields;
                const payload = {
                    campaignId,
                    image: key,
                    crop: cropString,
                };
                // previewImg.style.display = 'none';
                const cropResp = await saveCampaignPhotoCrop(
                    payload,
                    campaignId
                );

                // Create thumbnail
                const croppedThumb = await resizeCropped(file);

                const thumbFilename =
                    filenameParts[0] +
                    unixSeconds +
                    '_thumb.' +
                    filenameParts[1];

                // Prepare new signed url for thumbnail
                const thumbData = await fetchThumbPhotoPresignedUrl(
                    thumbFilename,
                    campaignId
                );

                // Upload thumbnail to s3
                await uploadFile(thumbData.fields, thumbData.url, croppedThumb);

                // Save thumbnail crop to DB
                const thumbPayload = {
                    campaignId,
                    image: thumbData.fields.key,
                    crop: cropString,
                };
                await saveThumbPhotoCrop(thumbPayload, campaignId);
                setSavingCrop(false);
                // hidePreview();
                setShowCropper(false);

                campaignDispatch({
                    type: 'set_campaign_photo_url',
                    payload: cropResp?.cropped_url_cache,
                });

                console.log(
                    'setting value update_photo to TRUE for updating WP'
                );
                updatePhoto(true);

                // campaignImg.src = json.cropped_url_cache;
                return cropResp;
            } catch (e) {
                console.log('e: ', e);
                // setSavingCrop(false);
            }
        };

        const handleCrop = async () => {
            const cropper = cropperRef.current?.cropper;
            const roundedData = cropper.getData(true);
            const { x, y, width, height } = roundedData;
            const cropString = `${x},${y},${x + width},${y + height}`;
            const saveCropResp = await saveCrop(cropString);
        };

        if (savingCrop) {
            return (
                <>
                    <p className='text-center'>Saving cropped image</p>
                    <p className='text-center'>
                        <Uploading />
                    </p>
                </>
            );
        }
        return (
            <>
                <div
                    className='preview-img'
                    style={{
                        width: 243,
                        height: 243,
                        overflow: 'hidden',
                        display: 'none',
                    }}
                />
                <Cropper
                    viewMode={2}
                    src={imageUrl}
                    style={{ height: 400, width: '100%' }}
                    guides={true}
                    ref={cropperRef}
                    aspectRatio={1}
                    preview={'.preview-img'}
                    rotatable={true}
                    className='mb-4'
                />
                <Button
                    color='outline-dark'
                    onClick={() => setShowCropper(false)}
                >
                    Cancel
                </Button>
                <Button
                    color='primary'
                    onClick={() => handleCrop()}
                    disabled={savingCrop}
                >
                    Save Image
                </Button>
            </>
        );
    };

    const ImgDropzone = () => {
        const campaignId = campaign?.campaignDetail?.campaign_id;
        const handleDrop = (files) => {
            files.forEach(async (file) => {
                try {
                    const filenameParts = file.name.split('.');
                    const ext = filenameParts[filenameParts.length - 1];
                    const name = filenameParts[0]
                        .replace(/\s/g, '_')
                        .replace(/(?!\w)./g, '');
                    const newFilename = `${name}.${ext}`;

                    setSavingCampaignImage(true);

                    const fileName = newFilename;
                    const { fields, url } =
                        await fetchCampaignPhotoPresignedUrl(
                            fileName,
                            campaignId
                        );

                    console.log('fields: ', fields);
                    console.log('url: ', url);
                    // Resize to max 2000 width
                    const resized = await resizeImage(file);
                    const resp = await uploadFile(fields, url, resized);
                    const { key } = fields;
                    const payload = {
                        campaignId,
                        image: key,
                        filename: fileName,
                    };
                    const saveResp = await saveCampaignPhoto(
                        payload,
                        campaignId
                    );
                    setSavedPicture(saveResp);
                    setImageUrl(saveResp.image);
                    setShowCropper(true);
                    setSavingCampaignImage(false);
                } catch (e) {
                    console.log('there was an error', e);
                    setSavingCampaignImage(false);
                }
            });
        };

        const photoInfo = campaign?.campaignPhotoInfo;

        const dropzoneMessage = () => {
            const campaignPhotoUrl =
                photoInfo?.photo_thumb_url ||
                photoInfo?.photo_url ||
                defaultProfileImg;
            // console.log('campaignPhotoUrl: ', campaignPhotoUrl);
            return (
                <div className='campaign-photo-dropzone text-center'>
                    {savingCampaignImage ? (
                        <Uploading />
                    ) : (
                        <div>
                            {campaign?.campaignPhotoInfo?.photo_url ? (
                                <div
                                    className='position-relative mx-auto'
                                    style={{ width: '220px' }}
                                >
                                    <img
                                        src={campaignPhotoUrl}
                                        alt={`${campaign?.patient?.person?.first_name} ${campaign?.patient?.person?.last_name}`}
                                        className='profile-photo'
                                    />
                                    <div className='small my-2'>
                                        {getFilename(
                                            campaign?.campaignPhotoInfo
                                                ?.photo_url
                                        )}
                                    </div>
                                    <button
                                        type='button'
                                        className='text-danger fs-4 btn btn-link p-0 position-absolute top-0 end-0'
                                        onClick={deleteCampaignPhoto}
                                    >
                                        {renderIcon('close')}
                                    </button>
                                </div>
                            ) : (
                                <div className='text-primary fs-4'>
                                    {renderIcon('upload')}
                                </div>
                            )}
                        </div>
                    )}

                    {!campaign?.campaignPhotoInfo?.photo_url && (
                        <p className='fs-6'>
                            <strong>Click to upload</strong> or drag and drop
                            <br /> PNG, JPG, or GIF (max 2000 x 2000px)
                        </p>
                    )}
                </div>
            );
        };

        useEffect(() => {
            if (campaign?.campaignPhotoInfo?.photo_url) {
                setIsDisabled(true);
            } else {
                setIsDisabled(false);
            }
        }, [campaign?.campaignPhotoInfo?.photo_url, savedPicture]);

        return (
            <>
                <Dropzone
                    onDrop={handleDrop}
                    accept={{ 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] }}
                    multiple={false}
                    disabled={isDisabled}
                >
                    {({ getRootProps, getInputProps }) => (
                        <Card
                            style={{
                                border: '2px dashed #949599',
                                background: '#F0F6F6',
                            }}
                        >
                            <CardBody>
                                <div {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    {dropzoneMessage()}
                                </div>
                            </CardBody>
                        </Card>
                    )}
                </Dropzone>
                <Modal
                    isOpen={showCropper}
                    toggle={() => setShowCropper(false)}
                >
                    <h3 className='text-center'>Edit your photo</h3>
                    <ModalBody>
                        <ShowCropper />
                    </ModalBody>
                </Modal>
            </>
        );
    };

    return <ImgDropzone />;
};
